import {ChangeDetectionStrategy, Component, computed, EventEmitter, input, Output, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {RowSelection} from '@app/model-composer/models/customization.model';
import {MaterialColorItemComponent} from '@app/model-composer/components/material-color-item.component';

@Component({
  selector: 'app-material-color-grid',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslatePipe, MaterialColorItemComponent],
  styles: [
    `
      @import 'tailwindcss' prefix(ipe);
      @theme {
        --font-display: 'ABC Walter Neue', sans-serif;
        --font-roboto: 'ABC Walter Neue', sans-serif;
        --font-sans: 'ABC Walter Neue', sans-serif;
      }
    `
  ],
  template: `
    @let sel = selectedKey();
    @let standardItems = visibleItems();
    @let etoItems = etoItemsList();
    @let hasEto = etoItems.length > 0;
    @let showEto = showEtoItems();
    @let shouldShowEtoToggle = hasEto && !hideEtoToggle();
    @let shouldRenderEtoItems = renderEtoItems();

    <div class="ipe:flex ipe:flex-col ipe:gap-4 ipe:mt-4">
      @if (standardItems.length > 0) {
        <div class="ipe:grid ipe:grid-cols-5 ipe:gap-y-4">
          @for (item of standardItems; track trackById) {
            @let key = toKey(item);

            <app-material-color-item
              [data]="item"
              size="md"
              [active]="key === sel"
              [displayName]="true"
              (pressed)="select.emit($event)"
            />
          }
        </div>
      }

      @if (shouldShowEtoToggle) {
        <div class="ipe:flex ipe:flex-col ipe:gap-4">
          <div class="ipe:flex ipe:justify-center">
            <button
              type="button"
              class="
                ipe:inline-flex ipe:items-center ipe:gap-2
                ipe:px-4 ipe:py-2.5
                ipe:border ipe:border-gray-300
                ipe:bg-white
                ipe:text-[11px] ipe:tracking-[0.18em] ipe:uppercase
                ipe:text-gray-700
                ipe:transition-all ipe:duration-200
                ipe:hover:border-gray-500
                ipe:hover:text-black
                ipe:hover:shadow-sm
                ipe:cursor-pointer
              "
              (click)="toggleEtoItems()"
            >
              <span>{{ 'MATERIALI_A_RICHIESTA' | translate }}</span>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="ipe:w-3 ipe:h-3 ipe:transition-transform ipe:duration-300 ipe:ease-out"
                [ngClass]="showEto ? 'ipe:rotate-180' : 'ipe:rotate-0'"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          </div>
        </div>
      }

      @if (hasEto && shouldRenderEtoItems) {
        <div class="ipe:flex ipe:flex-col ipe:gap-4 ipe:pt-4">
          @if (shouldShowEtoToggle) {
            <div class="ipe:flex ipe:justify-center">
              <div class="ipe:text-[10px] ipe:tracking-[0.16em] ipe:text-gray-400">
                {{ etoItems.length }} {{ 'MATERIALI_A_RICHIESTA' | translate | lowercase }}
              </div>
            </div>
          }
          <div class="ipe:grid ipe:grid-cols-5 ipe:gap-y-4">
            @for (item of etoItems; track trackById) {
              @let key = toKey(item);

              <app-material-color-item
                [data]="item"
                size="md"
                [active]="key === sel"
                [displayName]="true"
                (pressed)="select.emit($event)"
              />
            }
          </div>
        </div>
      }
    </div>
  `,
})
export class MaterialColorGridComponent {
  readonly items = input<any[]>([]);
  readonly selected = input<RowSelection | undefined>(undefined);
  readonly hideEtoToggle = input(false);
  readonly showEtoItems = signal(false);

  @Output() select = new EventEmitter<any>();

  readonly selectedKey = computed(() => {
    const s = this.selected()?.materialColor;
    return s ? `${s.materialId}-${s.colorId}` : null;
  });

  readonly visibleItems = computed(() =>
    this.items().filter(item => item?.eto !== true)
  );

  readonly etoItemsList = computed(() =>
    this.items().filter(item => item?.eto === true)
  );

  readonly renderEtoItems = computed(() => {
    if (this.hideEtoToggle()) {
      return this.etoItemsList().length > 0;
    }

    return this.showEtoItems();
  });

  trackById = (_: number, item: any) => this.toKey(item);

  toKey(item: any): string {
    return `${item.material.materialId}-${item.color.colorId}`;
  }

  toggleEtoItems(): void {
    this.showEtoItems.update(value => !value);
  }
}
